import React from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import toast from "react-hot-toast";
import { Copy } from "lucide-react";

import StatusBadge from "../../../components/StatusBadge";
import { Separator } from "../../../components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../components/ui/sheet";
import { formatLongDate } from "../../../utils/formatters";
import { depositNairaValue } from "../../../utils/money";
import { formatAmount } from "../../../utils/wordFunctions";
import type { DepositRow } from "./depositColumns";

type Props = {
  deposit: DepositRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const Row = ({
  label,
  value,
  copyValue,
  copyLabel,
}: {
  label: string;
  value: React.ReactNode;
  copyValue?: string;
  copyLabel?: string;
}) => (
  <div className="flex items-start justify-between gap-4 py-3">
    <span className="shrink-0 text-[13px] font-medium text-muted-foreground">
      {label}
    </span>
    <span className="flex min-w-0 items-center gap-2 text-right text-[13px] font-semibold text-foreground">
      <span className="truncate">{value}</span>
      {copyValue && (
        <CopyToClipboard
          text={copyValue}
          onCopy={() => toast.success(`${copyLabel ?? label} copied`)}
        >
          <button type="button" aria-label={`Copy ${label}`} className="shrink-0">
            <Copy className="size-3.5 text-muted-foreground transition-colors hover:text-primary" />
          </button>
        </CopyToClipboard>
      )}
    </span>
  </div>
);

const DepositDetailSheet = ({ deposit, open, onOpenChange }: Props) => {
  if (!deposit) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto font-manrope mds:max-w-[420px]">
        <SheetHeader className="pr-10 text-left">
          <SheetTitle className="font-sora text-[20px]">
            {formatAmount(deposit.amount)}{" "}
            <span className="uppercase">{deposit.currency}</span>
          </SheetTitle>
          <SheetDescription>
            ${formatAmount(deposit.usdtAmount)} received
          </SheetDescription>
          <StatusBadge status="COMPLETED" className="mt-2 w-fit" />
        </SheetHeader>

        <Separator className="my-4" />

        <div className="divide-y">
          <Row
            label="Date"
            value={formatLongDate(deposit.processedAt ?? deposit.createdAt)}
          />
          <Row label="Coin" value={(deposit.currency ?? "—").toUpperCase()} />
          <Row label="Network" value={(deposit.network ?? "—").toUpperCase()} />
          <Row
            label="Amount"
            value={`${formatAmount(deposit.amount)} ${(deposit.currency ?? "").toUpperCase()}`}
          />
          <Row label="Amount in USD" value={`$${formatAmount(deposit.usdtAmount)}`} />
          <Row
            label="Naira value"
            value={`${formatAmount(depositNairaValue(deposit))} NGN`}
          />
          <Row
            label="To address"
            value={deposit.toAddress ?? "—"}
            copyValue={deposit.toAddress}
            copyLabel="Address"
          />
          <Row
            label="Transaction ID"
            value={deposit.txid ?? "—"}
            copyValue={deposit.txid}
            copyLabel="Transaction ID"
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DepositDetailSheet;
